import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { AlertController } from '@ionic/angular';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError } from 'rxjs/internal/operators/catchError';
import { of } from 'rxjs';

// Declare OneSignal as any to avoid TypeScript import errors
// OneSignal cordova plugin is only available at runtime on native devices
declare var OneSignal: any;

@Injectable({
  providedIn: 'root',
})
export class OnesignalService {
  public playerID: any;
  private isNative: boolean;

  constructor(private alertCtrl: AlertController, private http: HttpClient) {
    this.isNative = Capacitor.isNativePlatform();
  }

  async OneSignalInit() {
    if (this.isNative) {
      try {
        OneSignal.initialize(environment.onesignal.appId);
        this.playerID = await OneSignal.User.getExternalId();
        console.log('OneSignal Native Player ID:', this.playerID);

        let myClickListener = async (event: any) => {
          let notificationData = JSON.stringify(event);
          console.log('notification data: ', notificationData);
        };
        OneSignal.Notifications.addEventListener('click', myClickListener);
      } catch (e) {
        console.error('OneSignal initialization error:', e);
      }
    } else {
      // PWA / Web Initialization
      try {
        const OneSignalWeb = (window as any).OneSignal || [];
        OneSignalWeb.push(() => {
          OneSignalWeb.init({
            appId: environment.onesignal.appId,
            allowLocalhostAsSecureOrigin: true,
          });
        });
        console.log('OneSignal PWA initialized');
      } catch (e) {
        console.error('OneSignal PWA initialization error:', e);
      }
    }
  }

  // onesignal notification permission
  async OneSignalIOSPermission() {
    if (!this.isNative) {
      // For PWA, OneSignal handles prompts via init if configured, 
      // or we can call showSlidedownPrompt()
      return;
    }

    try {
      if (Capacitor.getPlatform() == 'ios') {
        const ios_permission = await OneSignal.Notifications.permissionNative();
        // OSNotificationPermission.Authorized = 2
        if (ios_permission != 2) {
          this.OneSignalPermission();
        } else {
          this.requestPermission();
        }
      } else {
        // for android
        this.OneSignalPermission();
      }
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * Set External ID for targeted notifications 
   * (e.g. using Firebase UID)
   */
  async setExternalId(uid: string) {
    if (this.isNative) {
      OneSignal.login(uid);
    } else {
      const OneSignalWeb = (window as any).OneSignal || [];
      OneSignalWeb.push(() => {
        OneSignalWeb.login(uid);
      });
    }
    console.log('OneSignal External ID set to:', uid);
  }

  // Call this function when your app starts
  async OneSignalPermission(msg: string = '') {
    if (!this.isNative) return;

    try {
      const hasPermission = OneSignal.Notifications.hasPermission();
      console.log('hasPermission: ', hasPermission);
      if (!hasPermission) {
        // show prompt
        this.showAlert(msg);
      }
    } catch (e) {
      throw e;
    }
  }

  async requestPermission() {
    if (!this.isNative) {
      const OneSignalWeb = (window as any).OneSignal || [];
      OneSignalWeb.push(() => {
        OneSignalWeb.showSlidedownPrompt();
      });
      return;
    }

    try {
      const permission = await OneSignal.Notifications.canRequestPermission();
      console.log('permission: ', permission);
      if (permission) {
        const accepted = await OneSignal.Notifications.requestPermission(true);
        console.log('User accepted notifications: ' + accepted);
      } else {
        console.log('permission denied: ', permission);
        this.OneSignalPermission();
      }
    } catch (e) {
      throw e;
    }
  }

  showAlert(msg: string) {
    this.alertCtrl
      .create({
        header: `Allow Push Notifications${msg}`,
        message:
          'Please allow us to send you notifications to get latest offers and order updates...',
        buttons: [
          {
            text: "Don't Allow",
            role: 'cancel',
            handler: () => {
              console.log('Confirm Cancel');
              this.OneSignalPermission(" (It's mandatory)");
            },
          },
          {
            text: 'Allow',
            handler: () => {
              this.requestPermission();
            },
          },
        ],
      })
      .then((alertEl) => alertEl.present());
  }

  /**
   * Send notification via PaymentServer (Secure)
   */
  sendNotification(msg: string, title: string, externalIds: string[] = [], data: any = null) {
    const body: any = {
      message: msg,
      headings: title,
      externalIds: externalIds,
      data: data
    };

    const url = `${environment.serverUrl}send-notification`;

    return this.http.post<any>(url, body).pipe(
      catchError((error) => {
        console.error('Error sending notification via PaymentServer:', error);
        return of(null);
      })
    );
  }


}